import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { uploadFileToFirebase, updateUserProfileData } from '../services/firebase';
import { Camera, User, Mail, CreditCard, Save, Loader2, Coins } from 'lucide-react';
import { UserTier } from '../types';

interface UserProfileProps {
  onOpenPricing: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ onOpenPricing }) => {
  const { user, credits, tier, refreshCredits } = useAuth();
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [loading, setLoading] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!user) return null;

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateUserProfileData(user.uid, { displayName });
      await refreshCredits(); // Refresh context
      alert("Profile updated!");
    } catch (error) {
      console.error(error);
      alert("Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadingImg(true);
      try {
        const path = `users/${user.uid}/avatar/${Date.now()}_${file.name}`;
        const url = await uploadFileToFirebase(file, path);
        await updateUserProfileData(user.uid, { photoURL: url });
        await refreshCredits(); // Refresh context to show new image
      } catch (error) {
        console.error("Error uploading image:", error);
        alert("Failed to upload image.");
      } finally {
        setUploadingImg(false);
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="bg-gray-900/60 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
        
        {/* Banner */}
        <div className="h-32 bg-gradient-to-r from-blue-900/40 via-purple-900/40 to-blue-900/40 relative">
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
        </div>

        <div className="px-8 pb-8">
          <div className="relative flex justify-between items-end -mt-12 mb-8">
             {/* Avatar */}
             <div className="relative group">
               <div className="w-24 h-24 rounded-full border-4 border-gray-900 bg-gray-800 overflow-hidden shadow-lg relative">
                 {user.photoURL ? (
                   <img src={user.photoURL} alt="Profile" className="w-full h-full object-cover" />
                 ) : (
                   <div className="w-full h-full flex items-center justify-center bg-gray-700 text-gray-400">
                     <User className="w-10 h-10" />
                   </div>
                 )}
                 {uploadingImg && (
                   <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                     <Loader2 className="w-6 h-6 animate-spin text-white" />
                   </div>
                 )}
               </div>
               <button 
                 onClick={() => fileInputRef.current?.click()}
                 className="absolute bottom-0 right-0 p-2 bg-blue-600 rounded-full text-white hover:bg-blue-500 border border-gray-900 transition-colors shadow-lg"
                 title="Change Avatar"
               >
                 <Camera className="w-4 h-4" />
               </button>
               <input 
                 type="file" 
                 ref={fileInputRef} 
                 onChange={handleImageUpload} 
                 accept="image/*" 
                 className="hidden" 
               />
             </div>
             
             {/* Plan Badge */}
             <div className="flex flex-col items-end">
                <span className="text-xs text-gray-400 uppercase tracking-wider font-bold mb-1">Current Plan</span>
                <span className={`px-4 py-1.5 rounded-full border text-sm font-bold uppercase shadow-[0_0_15px_rgba(0,0,0,0.3)]
                  ${tier === 'pro' ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300' : 
                    tier === 'creator' ? 'bg-blue-500/20 border-blue-500/50 text-blue-300' : 
                    'bg-gray-700/50 border-gray-600 text-gray-300'}`}>
                  {tier}
                </span>
             </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
             {/* Edit Profile Form */}
             <div className="md:col-span-2 space-y-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                   <User className="w-5 h-5 text-blue-400" /> Personal Info
                </h3>
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                         <label className="text-sm text-gray-400">Display Name</label>
                         <input 
                           type="text" 
                           value={displayName}
                           onChange={(e) => setDisplayName(e.target.value)}
                           className="w-full bg-gray-800/50 border border-gray-700 rounded-xl p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                         />
                      </div>
                      <div className="space-y-2">
                         <label className="text-sm text-gray-400">Email (Read Only)</label>
                         <div className="flex items-center gap-2 w-full bg-gray-800/30 border border-gray-700/50 rounded-xl p-3 text-gray-500">
                            <Mail className="w-4 h-4" />
                            {user.email}
                         </div>
                      </div>
                   </div>
                   <button 
                     type="submit" 
                     disabled={loading}
                     className="px-6 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded-xl text-white font-medium transition-all flex items-center gap-2"
                   >
                     {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                     Save Changes
                   </button>
                </form>
             </div>

             {/* Credits & Billing */}
             <div className="space-y-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                   <CreditCard className="w-5 h-5 text-green-400" /> Balance
                </h3>
                <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-6 relative overflow-hidden group">
                   <div className="absolute top-0 right-0 w-24 h-24 bg-green-500/10 rounded-full blur-2xl -mr-10 -mt-10 transition-all group-hover:bg-green-500/20"></div>
                   
                   <p className="text-gray-400 text-sm mb-1">Available Credits</p>
                   <div className="flex items-baseline gap-2 mb-6">
                      <span className="text-4xl font-bold text-white tracking-tight">{credits}</span>
                      <span className="text-sm text-gray-500">videos</span>
                   </div>

                   <button 
                     onClick={onOpenPricing}
                     className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
                   >
                     <Coins className="w-4 h-4" /> Buy More
                   </button>
                   <p className="text-[10px] text-center text-gray-500 mt-3">
                     Payments secured by PayPal. 
                     <br/>Credits do not expire.
                   </p>
                </div>
             </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default UserProfile;